import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CompanySettings } from '@/types/firebase';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(amount);
}

export function generateInvoiceNumber(settings?: Partial<CompanySettings>, existingInvoices?: any[]): string {
  const invoiceSettings = settings?.invoiceSettings;
  let number = '';

  // Get the base number
  if (invoiceSettings?.invoiceStartingNumber) {
    // If we have existing invoices, find the max number and increment
    if (existingInvoices && existingInvoices.length > 0) {
      const numbers = existingInvoices
        .map(inv => {
          const match = inv.invoiceNumber.match(/(\d+)$/);
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n): n is number => n !== null);
      
      const maxNumber = numbers.length > 0 ? Math.max(...numbers) : invoiceSettings.invoiceStartingNumber;
      number = (maxNumber + 1).toString();
    } else {
      // If no existing invoices, use the starting number
      number = invoiceSettings.invoiceStartingNumber.toString();
    }
  } else {
    // Fallback to timestamp-based number if no settings
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    number = `${timestamp}${random}`;
  }

  // Add date prefix if enabled
  if (invoiceSettings?.enableDatePrefix) {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    number = `${year}${month}-${number}`;
  }

  // Apply zero padding
  if (invoiceSettings?.zeroPadding) {
    const numericPart = number.replace(/\D/g, '');
    number = number.replace(
      numericPart,
      numericPart.padStart(invoiceSettings.zeroPadding, '0')
    );
  }

  // Apply custom prefix if available
  if (invoiceSettings?.invoicePrefix) {
    number = `${invoiceSettings.invoicePrefix}-${number}`;
  }

  return number;
}

/**
 * Generates a secure random key for invoice payment URLs
 * The key is 32 characters long and uses a mix of letters and numbers
 * This makes it practically impossible to guess
 */
export function generatePaymentKey(): string {
  // Create a random string using a mix of characters
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const keyLength = 32;
  let result = '';
  
  try {
    // Use window.crypto for more secure random values if available
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(keyLength);
      window.crypto.getRandomValues(values);
      for (let i = 0; i < keyLength; i++) {
        result += characters.charAt(values[i] % characters.length);
      }
    } else {
      throw new Error('Crypto API not available');
    }
  } catch (_error) {
    // Fallback to Math.random (less secure but still usable)
    console.warn('Using fallback random generator for payment key');
    for (let i = 0; i < keyLength; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
  }
  
  return result;
}

export function calculateDueDate(date: string, terms: string): string {
  const baseDate = new Date(date);
  switch (terms) {
    case 'net_15':
      baseDate.setDate(baseDate.getDate() + 15);
      break;
    case 'net_30':
      baseDate.setDate(baseDate.getDate() + 30);
      break;
    case 'net_60':
      baseDate.setDate(baseDate.getDate() + 60);
      break;
    default:
      // due_on_receipt - same as invoice date
      break;
  }
  return baseDate.toISOString().split('T')[0];
}

/**
 * Formats a number as a percentage with specified decimal places
 * @param value The decimal value to format (e.g., 0.25 for 25%)
 * @param decimals The number of decimal places to show (default: 0)
 * @returns Formatted percentage string (e.g., "25%")
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Formats a date string into a user-friendly format (timezone-safe)
 * @param dateString The date string to format
 * @param options Optional Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(dateString: string | Date, options: Intl.DateTimeFormatOptions = {}): string {
  let date: Date;
  
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Handle YYYY-MM-DD format specifically to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day); // month is 0-indexed
  } else {
    date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  }
  
  // Get local date components without timezone conversion
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  // Create a new date using local components to avoid timezone shift
  const localDate = new Date(year, month, day);
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options
  };
  
  return new Intl.DateTimeFormat('en-US', defaultOptions).format(localDate);
}

/**
 * Generates a recurring invoice template number in the format REC-1, REC-2, etc.
 * This is separate from the regular invoice numbering system and only used for templates.
 * @param existingTemplates Existing invoice templates to determine the next number
 * @returns A string in the format REC-X where X is the next number in sequence
 */
export function generateRecurringTemplateNumber(existingTemplates?: any[]): string {
  // Default to REC-1 if no templates exist
  if (!existingTemplates || existingTemplates.length === 0) {
    return 'REC-1';
  }
  
  // Extract numbers from existing templates with REC- prefix
  const numbers = existingTemplates
    .map(template => {
      if (!template.invoiceNumber) return null;
      const match = template.invoiceNumber.match(/^REC-(\d+)$/);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n): n is number => n !== null);
  
  // If no matching REC-X numbers found, start with REC-1
  if (numbers.length === 0) {
    return 'REC-1';
  }
  
  // Find max number and increment by 1
  const maxNumber = Math.max(...numbers);
  return `REC-${maxNumber + 1}`;
}

/**
 * Gets today's date in YYYY-MM-DD format, always using local timezone
 * This ensures invoice dates are consistent regardless of server timezone
 */
export function getTodayInLocalTimezone(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date to YYYY-MM-DD format in local timezone
 * @param date Date object or string to format
 */
export function formatDateToLocalTimezone(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
